import { Select, TextInput } from '@octano/global-ui';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Col, Row } from 'reactstrap';
import RemoteSearchSelect from '../../../../../../../components/form/RemoteSearchSelect';
import { useValidations } from '../../../../../../../hooks/useValidations';
import { useSearchStudents } from '../../hooks/remoteSearchSelect/useSearchStudents';
import { useTexts } from '../../hooks/useTexts';
import { AssignedStudentStudyPlanEnrollment } from '../../types';

interface FormFieldsProps {
  fieldNames?: {
    studentId: string;
    studyPlanVersion: string;
    school: string;
    campus: string;
    schedule: string;
  };
}

export const AssignedStudentModalForm = ({
  fieldNames = {
    studentId: 'studentId',
    studyPlanVersion: 'studyPlanVersion',
    school: 'school', // facultad
    campus: 'campus', // sede
    schedule: 'schedule', // jornada
  },
}: FormFieldsProps) => {
  const texts = useTexts();
  const { watch, control, reset } = useFormContext();
  const { msgValidations } = useValidations();
  const searchStudents = useSearchStudents();

  const [studentIdSelected, studyPlanVersionIdSelected] = watch([
    fieldNames.studentId,
    fieldNames.studyPlanVersion,
  ]);

  // Planes de estudio del estudiante
  const studyplansFromStudent = useMemo(() => {
    if (!studentIdSelected?.value) {
      return [];
    }

    const student = studentIdSelected;
    if (!student || !student.hasOwnProperty('studyPlanEnrollments')) {
      return [];
    }

    return student.studyPlanEnrollments.map(
      (studyPlanEnroll: AssignedStudentStudyPlanEnrollment) => ({
        label: studyPlanEnroll.studyPlanVersion.name,
        value: studyPlanEnroll.enrollmentId,
        ...studyPlanEnroll,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentIdSelected?.value]);

  // Resetear campos de formulario, al cambiar de estudiante
  useEffect(() => {
    if (!studentIdSelected?.value) {
      return;
    }
    const singleStudyPlan =
      studyplansFromStudent.length === 1 ? studyplansFromStudent[0] : null;

    reset({
      studentId: studentIdSelected,
      studyPlanVersion: singleStudyPlan,
      campus: null,
      schedule: null,
      school: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentIdSelected?.value]);

  return (
    <Row xs={12}>
      <Col xs={12}>
        <RemoteSearchSelect
          label={texts.modal.form.studentId}
          name={fieldNames.studentId}
          filterOptions={searchStudents}
          control={control}
          rules={{ required: msgValidations.required }}
        />
      </Col>
      {studentIdSelected?.value && (
        <>
          <Col xs={12}>
            <Select
              label={texts.modal.form.studyPlanVersion}
              name={fieldNames.studyPlanVersion}
              options={studyplansFromStudent}
              control={control}
              rules={{
                required: msgValidations.required,
              }}
            />
          </Col>
          <Col xs={6}>
            <TextInput
              label={texts.modal.form.school}
              name={fieldNames.school}
              value={studyPlanVersionIdSelected?.school?.name}
              disabled
            />
          </Col>
          <Col xs={6}>
            <TextInput
              label={texts.modal.form.campus}
              name={fieldNames.campus}
              value={studyPlanVersionIdSelected?.campus?.name}
              disabled
            />
          </Col>
          <Col xs={6}>
            <TextInput
              label={texts.modal.form.schedule}
              name={fieldNames.schedule}
              value={studyPlanVersionIdSelected?.schedule?.name}
              disabled
            />
          </Col>
        </>
      )}
    </Row>
  );
};
